import React, { useContext, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import { useSupply } from "../context/SupplyContext";

const dateFormatter = new Intl.DateTimeFormat("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

const dateTimeFormatter = new Intl.DateTimeFormat("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
});

const matchStatusMetadata = {
    MATCH: {
        label: "Gleichlauf",
        classes: "bg-green-100 text-green-800 border-green-200",
    },
    QUANTITY_DEVIATION: {
        label: "Mengenabweichung",
        classes: "bg-amber-100 text-amber-800 border-amber-200",
    },
    PRICE_DEVIATION: {
        label: "Preisabweichung",
        classes: "bg-orange-100 text-orange-800 border-orange-200",
    },
};

const resolutionLabels = {
    MATCHED: "Abgeglichen",
    PENDING: "Offen",
    IN_CLARIFICATION: "In Klärung",
    ACCEPTED: "Akzeptiert",
};

const resolutionClasses = {
    Abgeglichen: "bg-green-100 text-green-800 border-green-200",
    Offen: "bg-amber-100 text-amber-800 border-amber-200",
    "In Klärung": "bg-orange-100 text-orange-800 border-orange-200",
    Akzeptiert: "bg-blue-100 text-blue-800 border-blue-200",
};

const historyActionLabels = {
    AUTO_CHECK: "Automatischer Abgleich",
    IN_CLARIFICATION: "Zur Klärung weitergeleitet",
    ACCEPTED: "Abweichung akzeptiert",
};

function InvoiceReviewDetailPage() {
    const { invoiceId } = useParams();
    const { invoices, acceptInvoiceDeviation, requestInvoiceClarification } = useSupply();
    const { userId, roles } = useContext(AuthContext);
    const [clarifyLineId, setClarifyLineId] = useState(null);
    const [clarifyNote, setClarifyNote] = useState("");

    const invoice = useMemo(() => invoices.find((item) => item.id === invoiceId), [invoiceId, invoices]);

    const currencyFormatter = useMemo(
        () =>
            new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: invoice?.currency ?? "EUR",
                minimumFractionDigits: 2,
            }),
        [invoice?.currency],
    );

    if (!invoice) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-16 text-center">
                <p className="text-sm text-slate-500">Die angeforderte Rechnung wurde nicht gefunden.</p>
                <Link
                    to="/supply/invoices"
                    className="mt-4 inline-flex items-center rounded-md border border-blue-600 px-3 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50"
                >
                    Zur Übersicht
                </Link>
            </div>
        );
    }

    const deviatingLines = invoice.lines.filter((line) => line.matchStatus !== "MATCH");
    const matchedLines = invoice.lines.length - deviatingLines.length;
    const roleLabel = Array.isArray(roles) ? roles[0] : roles;
    const userDisplayName = userId || roleLabel || "Unbekannter Nutzer";

    const handleAccept = (lineId) => {
        acceptInvoiceDeviation(invoice.id, lineId, { user: userDisplayName });
        if (clarifyLineId === lineId) {
            setClarifyLineId(null);
            setClarifyNote("");
        }
    };

    const handleClarifyClick = (line) => {
        setClarifyLineId(line.id);
        setClarifyNote(line.resolution?.note ?? "");
    };

    const handleClarifySubmit = (event, lineId) => {
        event.preventDefault();
        const note = clarifyNote.trim();
        if (!note) {
            return;
        }

        requestInvoiceClarification(invoice.id, lineId, {
            user: userDisplayName,
            note,
        });
        setClarifyLineId(null);
        setClarifyNote("");
    };

    const handleClarifyCancel = () => {
        setClarifyLineId(null);
        setClarifyNote("");
    };

    return (
        <div className="mx-auto max-w-6xl px-4 py-8">
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-start md:justify-between">
                <div>
                    <Link to="/supply/invoices" className="text-sm font-medium text-blue-600 hover:text-blue-700">
                        ← Zurück zur Übersicht
                    </Link>
                    <h1 className="mt-2 text-3xl font-semibold text-slate-900">Rechnung {invoice.invoiceNumber}</h1>
                    <p className="mt-1 text-sm text-slate-600">{invoice.supplierName}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span>Bestellung {invoice.purchaseOrderNumber}</span>
                        <span>Wareneingang {invoice.goodsReceiptNumber}</span>
                        <span>Importquelle {invoice.importSource}</span>
                    </div>
                </div>
                <div className="space-y-2 text-right">
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        {invoice.status}
                    </span>
                    <div className="text-sm text-slate-500">
                        Fällig am {dateFormatter.format(new Date(invoice.dueDate))}
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                        {currencyFormatter.format(invoice.amount)}
                    </div>
                </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <DetailTile label="Rechnungsdatum" value={dateFormatter.format(new Date(invoice.invoiceDate))} />
                <DetailTile label="Bestellung" value={invoice.purchaseOrderNumber} />
                <DetailTile label="Wareneingang" value={invoice.goodsReceiptNumber} />
                <DetailTile
                    label="Abgleich"
                    value={`${matchedLines} von ${invoice.lines.length} Positionen ohne Abweichung`}
                />
            </div>

            <section className="mt-8 rounded-lg border border-slate-200 bg-white shadow-sm">
                <header className="border-b border-slate-200 px-6 py-4">
                    <h2 className="text-lg font-semibold text-slate-900">3-Way-Match-Ergebnis</h2>
                    <p className="text-sm text-slate-500">
                        Automatischer Abgleich zwischen Bestellung, Wareneingang und Rechnung.
                    </p>
                </header>
                <div className="grid gap-6 px-6 py-6 md:grid-cols-3">
                    <MatchSummaryTile
                        title="Bestellung"
                        reference={invoice.purchaseOrderNumber}
                        description={`${matchedLines} Positionen bestätigt`}
                    />
                    <MatchSummaryTile
                        title="Wareneingang"
                        reference={invoice.goodsReceiptNumber}
                        description={
                            deviatingLines.length > 0
                                ? `${deviatingLines.length} Position(en) mit Differenz`
                                : "Keine Differenzen"
                        }
                    />
                    <MatchSummaryTile
                        title="Rechnung"
                        reference={invoice.invoiceNumber}
                        description={currencyFormatter.format(invoice.amount)}
                        highlight={deviatingLines.length > 0}
                    />
                </div>
            </section>

            <section className="mt-10">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <h2 className="text-xl font-semibold text-slate-900">Positionen &amp; Abweichungen</h2>
                    {deviatingLines.length > 0 ? (
                        <p className="text-sm text-amber-700">
                            {deviatingLines.length} Position(en) benötigen Ihre Entscheidung.
                        </p>
                    ) : (
                        <p className="text-sm text-green-700">Alle Positionen wurden ohne Abweichung abgeglichen.</p>
                    )}
                </div>

                <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left">Pos.</th>
                                <th scope="col" className="px-4 py-3 text-left">Artikel</th>
                                <th scope="col" className="px-4 py-3 text-left">Bestellung</th>
                                <th scope="col" className="px-4 py-3 text-left">Wareneingang</th>
                                <th scope="col" className="px-4 py-3 text-left">Rechnung</th>
                                <th scope="col" className="px-4 py-3 text-left">Abweichung</th>
                                <th scope="col" className="px-4 py-3 text-left">Status</th>
                                <th scope="col" className="px-4 py-3 text-left">Aktion</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {invoice.lines.map((line) => {
                                const matchMeta = matchStatusMetadata[line.matchStatus] ?? {
                                    label: line.matchStatus,
                                    classes: "bg-slate-100 text-slate-700 border-slate-200",
                                };
                                const resolutionLabel = resolutionLabels[line.resolution?.status] ??
                                    (line.matchStatus === "MATCH" ? resolutionLabels.MATCHED : resolutionLabels.PENDING);
                                const resolutionClass =
                                    resolutionClasses[resolutionLabel] ?? "bg-slate-100 text-slate-700 border-slate-200";
                                const history = line.history ?? [];
                                const isDeviation = line.matchStatus !== "MATCH";

                                return (
                                    <React.Fragment key={line.id}>
                                        <tr className={isDeviation ? "bg-amber-50/60" : "bg-white"}>
                                            <td className="px-4 py-4 font-medium text-slate-700">{line.position}</td>
                                            <td className="px-4 py-4">
                                                <div className="font-semibold text-slate-900">{line.description}</div>
                                                <div className="text-xs text-slate-500">{line.itemNumber}</div>
                                                <span
                                                    className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${matchMeta.classes}`}
                                                >
                                                    {matchMeta.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                <div>{`${line.orderedQty} ${line.unit}`}</div>
                                                <div className="text-xs text-slate-500">
                                                    {currencyFormatter.format(line.orderedPrice)} pro Einheit
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                <div>{`${line.receivedQty} ${line.unit}`}</div>
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                <div>{`${line.invoicedQty} ${line.unit}`}</div>
                                                <div className="text-xs text-slate-500">
                                                    {currencyFormatter.format(line.invoicedPrice)} pro Einheit
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                {line.deviation ? (
                                                    <>
                                                        <div className="font-medium text-amber-700">{line.deviation.type}</div>
                                                        <div className="text-xs text-slate-500">{line.deviation.description}</div>
                                                        {line.deviation.difference && (
                                                            <div className="text-xs text-slate-500">
                                                                Differenz: {line.deviation.difference}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-green-700">Keine Abweichung</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                <span
                                                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${resolutionClass}`}
                                                >
                                                    {resolutionLabel}
                                                </span>
                                                {line.resolution?.user && (
                                                    <div className="mt-2 text-[11px] text-slate-500">
                                                        {line.resolution.status === "ACCEPTED" ? "Akzeptiert" : "Bearbeitet"} von {line.resolution.user}
                                                        {line.resolution.timestamp
                                                            ? ` am ${dateTimeFormatter.format(new Date(line.resolution.timestamp))}`
                                                            : ""}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                {isDeviation ? (
                                                    <div className="flex flex-col gap-2 md:flex-row">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAccept(line.id)}
                                                            className="inline-flex items-center justify-center rounded-md border border-blue-600 px-3 py-2 text-xs font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400 disabled:hover:bg-transparent"
                                                            disabled={line.resolution?.status === "ACCEPTED"}
                                                        >
                                                            Akzeptieren
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleClarifyClick(line)}
                                                            className="inline-flex items-center justify-center rounded-md border border-amber-600 px-3 py-2 text-xs font-semibold text-amber-700 transition hover:bg-amber-50"
                                                        >
                                                            Klären
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-slate-400">Keine Aktion nötig</span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={8} className="px-4 pb-6 pt-0 text-xs text-slate-600">
                                                <div className="border-t border-slate-200 pt-4">
                                                    {history.length > 0 ? (
                                                        <ul className="space-y-2">
                                                            {history.map((entry, index) => (
                                                                <li key={index} className="flex gap-2">
                                                                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-slate-300" aria-hidden />
                                                                    <div>
                                                                        <p className="text-[13px] text-slate-700">
                                                                            <span className="font-medium">
                                                                                {historyActionLabels[entry.action] ?? entry.action}
                                                                            </span>{" "}
                                                                            durch {entry.user} am {dateTimeFormatter.format(new Date(entry.timestamp))}
                                                                        </p>
                                                                        {entry.note && <p className="text-[12px] text-slate-500">{entry.note}</p>}
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-[12px] text-slate-500">Noch keine Aktivitäten protokolliert.</p>
                                                    )}

                                                    {clarifyLineId === line.id && (
                                                        <form
                                                            className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4"
                                                            onSubmit={(event) => handleClarifySubmit(event, line.id)}
                                                        >
                                                            <label className="flex flex-col gap-2 text-sm text-amber-900">
                                                                <span className="font-semibold">Notiz für Rückfrage</span>
                                                                <textarea
                                                                    value={clarifyNote}
                                                                    onChange={(event) => setClarifyNote(event.target.value)}
                                                                    rows={3}
                                                                    className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
                                                                    placeholder="Bitte kurze Begründung für die Klärung eintragen"
                                                                    required
                                                                />
                                                            </label>
                                                            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
                                                                <button
                                                                    type="submit"
                                                                    className="inline-flex items-center justify-center rounded-md border border-amber-600 bg-amber-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-amber-700"
                                                                >
                                                                    An Lieferant senden
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleClarifyCancel}
                                                                    className="inline-flex items-center justify-center rounded-md border border-transparent px-3 py-2 text-xs font-semibold text-amber-700 hover:text-amber-800"
                                                                >
                                                                    Abbrechen
                                                                </button>
                                                            </div>
                                                        </form>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

function DetailTile({ label, value }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-2 text-sm font-medium text-slate-900">{value}</p>
        </div>
    );
}

function MatchSummaryTile({ title, reference, description, highlight = false }) {
    return (
        <div
            className={`rounded-lg border p-4 shadow-sm ${
                highlight ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"
            }`}
        >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
            <p className="mt-2 text-base font-semibold text-slate-900">{reference}</p>
            <p className="mt-1 text-xs text-slate-600">{description}</p>
        </div>
    );
}

export default InvoiceReviewDetailPage;
